'use client';

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { Centrifuge, SubscriptionState, type Subscription, type PublicationContext } from 'centrifuge';
import { useApiClient } from './ApiContext';
import { formatApiError } from '../utils/apiError';

// Channel prefix from env var (e.g., 'dev', 'prod')
const CHANNEL_PREFIX = process.env.NEXT_PUBLIC_CENTRIFUGO_CHANNEL_PREFIX || '';

const IS_DEBUG = process.env.NODE_ENV !== 'production';

/** Пожизненный лог сокета нужен только при отладке: в проде он крутится на каждой
 *  публикации drops_feed и стоит главного потока. */
const debugLog = (...args: unknown[]): void => {
  if (IS_DEBUG) console.log(...args);
};

/** Столько обрывов за сессию уже не объяснить мобильной сетью — это цикл переподключений. */
const RECONNECT_STORM_THRESHOLD = 25;

/** 103 permission denied, 102 unknown channel — сервер не передумает, повтор бессмысленен. */
const PERMANENT_SUBSCRIPTION_CODES = new Set([102, 103]);

const isPermanentSubscriptionError = (ctx: { error?: { code?: number } }): boolean =>
  typeof ctx.error?.code === 'number' && PERMANENT_SUBSCRIPTION_CODES.has(ctx.error.code);

export interface CentrifugoContextValue {
  isConnected: boolean;
  channelPrefix: string;
  subscribe: (channel: string, onMessage: (data: unknown) => void) => () => void;
}

// Exported so tests / Storybook can inject a mock value (emit scripted socket events).
export const CentrifugoContext = createContext<CentrifugoContextValue | null>(null);

interface CentrifugoProviderProps {
  children: ReactNode;
  /** When true, use the public (read-only) token endpoint — for anonymous
   * landing visitors. Defaults to false (authenticated TMA user). */
  anonymous?: boolean;
}

export const CentrifugoProvider: React.FC<CentrifugoProviderProps> = ({ children, anonymous = false }) => {
  const apiClient = useApiClient();
  const [isConnected, setIsConnected] = useState(false);
  const [channelPrefix, setChannelPrefix] = useState(CHANNEL_PREFIX);
  const centrifugeRef = useRef<Centrifuge | null>(null);
  const subscriptionsRef = useRef<Map<string, Subscription>>(new Map());
  // Per-channel handler count so cleanup tears the underlying Subscription
  // down only when the LAST consumer unsubscribes. Without this, the
  // private user# subscription stayed alive across auth flips because the
  // map only got cleared on full provider unmount (PR6 fixup #L7).
  const handlerCountRef = useRef<Map<string, number>>(new Map());
  const deniedChannelsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Use env var if set, otherwise build URL dynamically for local dev or use production default
    let wsUrl = process.env.NEXT_PUBLIC_CENTRIFUGO_WS_URL;

    if (!wsUrl) {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${protocol}//${window.location.host}/centrifugo/connection/websocket`;
    }

    const fetchToken = async () => {
      const response = anonymous
        ? await apiClient.api.realtimeControllerGetPublicToken()
        : await apiClient.api.realtimeControllerGetToken();
      setChannelPrefix(response.channelPrefix);
      return response.token;
    };

    const centrifuge = new Centrifuge(wsUrl, {
      getToken: async () => {
        try {
          return await fetchToken();
        } catch (error) {
          console.error('Failed to get Centrifugo token:', formatApiError(error));
          throw error;
        }
      },
    });

    centrifuge.on('connected', (ctx) => {
      debugLog('[Centrifugo] ✅ Connected', ctx);
      setIsConnected(true);
    });

    centrifuge.on('disconnected', (ctx) => {
      debugLog('[Centrifugo] ⚠️ Disconnected', ctx);
      setIsConnected(false);
    });

    centrifuge.on('connecting', (ctx) => {
      debugLog('[Centrifugo] 🔄 Connecting...', ctx);
    });

    // Обрыв сокета — рядовое событие мобильной сети, и centrifuge-js переподключается сам.
    // Раньше каждый обрыв уходил console.error → RUM считал его ошибкой (десятки тысяч в сутки
    // с одной вкладки) и топил в себе настоящие ошибки. Ниже: рядовые обрывы — warn, а вот
    // непрерывный цикл переподключений остаётся видимым — ровно одной ошибкой на сессию.
    let transportErrors = 0;
    centrifuge.on('error', (ctx) => {
      transportErrors += 1;
      if (transportErrors === RECONNECT_STORM_THRESHOLD) {
        console.error(`Centrifugo reconnect storm: ${transportErrors} обрывов за сессию`, ctx);
        return;
      }
      if (transportErrors < RECONNECT_STORM_THRESHOLD) console.warn('Centrifugo error:', ctx);
    });

    centrifugeRef.current = centrifuge;
    centrifuge.connect();

    return () => {
      subscriptionsRef.current.forEach((sub) => sub.unsubscribe());
      subscriptionsRef.current.clear();
      handlerCountRef.current.clear();
      centrifuge.disconnect();
    };
    // `anonymous` MUST be in deps — when it flips (e.g. AuthBootstrap
    // markAuthed() flow on a cold TMA boot) we need to reconnect with the
    // private token so the user#<id> channel actually authorizes. Prior
    // PR6 omitted it; the socket stayed anonymous and unlock toasts never
    // fired (PR6 fixup #H2).
  }, [apiClient, anonymous]);

  const subscribe = useCallback((channel: string, onMessage: (data: unknown) => void) => {
    const centrifuge = centrifugeRef.current;
    if (!centrifuge) {
      console.warn('[Centrifugo] Cannot subscribe, client not initialized');
      return () => {};
    }

    debugLog(`[Centrifugo] 📡 Attempting to subscribe to: ${channel}`);

    // Канал, на который сервер ответил окончательным отказом (нет прав, нет канала),
    // переподписывать нельзя: каждый ремаунт слушателя ловил бы тот же отказ по кругу —
    // в логах Centrifugo это видно как «permission denied» раз в пару секунд у одного клиента.
    if (deniedChannelsRef.current.has(channel)) return () => {};

    // Reuse an existing Subscription. Our map is the primary record, but the
    // centrifuge client keeps its OWN registry and newSubscription() throws
    // "Subscription to the channel … already exists" if that registry still
    // holds the channel. If the two ever diverge (e.g. a prior teardown that
    // unsubscribed without removing it), fall back to the client's registry
    // instead of allocating a duplicate and crashing the render.
    let subscription = subscriptionsRef.current.get(channel) ?? centrifuge.getSubscription(channel) ?? undefined;

    if (!subscription) {
      subscription = centrifuge.newSubscription(channel);

      subscription.on('subscribed', (ctx) => {
        debugLog(`[Centrifugo] ✅ Successfully subscribed to: ${channel}`, ctx);
      });
      subscription.on('subscribing', (ctx) => {
        debugLog(`[Centrifugo] 🔄 Subscribing to: ${channel}`, ctx);
      });
      subscription.on('unsubscribed', (ctx) => {
        debugLog(`[Centrifugo] ⚠️ Unsubscribed from: ${channel}`, ctx);
      });
      subscription.on('error', (ctx) => {
        if (isPermanentSubscriptionError(ctx)) {
          deniedChannelsRef.current.add(channel);
          console.warn(`[Centrifugo] подписка на ${channel} отклонена окончательно:`, ctx.error);
          return;
        }
        debugLog(`[Centrifugo] ❌ Subscription error on ${channel}:`, ctx);
      });
    } else {
      debugLog(`[Centrifugo] ♻️ Reusing existing subscription: ${channel}`);
    }

    subscriptionsRef.current.set(channel, subscription);
    if (subscription.state === SubscriptionState.Unsubscribed) {
      subscription.subscribe();
    }

    const handler = (ctx: PublicationContext) => {
      debugLog(`[Centrifugo] 📨 Message received on ${channel}`, ctx.data);
      onMessage(ctx.data);
    };

    subscription.on('publication', handler);
    handlerCountRef.current.set(channel, (handlerCountRef.current.get(channel) ?? 0) + 1);
    console.log(`[Centrifugo] ✅ Handler attached to ${channel}`);

    return () => {
      console.log(`[Centrifugo] 🔌 Removing handler from ${channel}`);
      const sub = subscriptionsRef.current.get(channel);
      sub?.removeListener('publication', handler);
      const remaining = (handlerCountRef.current.get(channel) ?? 1) - 1;
      if (remaining <= 0) {
        // Last consumer detached — remove from BOTH our map and the
        // centrifuge client's registry. unsubscribe() alone leaves the
        // channel registered in the client, so the next subscribe() to it
        // hits "already exists" and throws mid-render. removeSubscription()
        // unsubscribes internally, then drops it from the registry.
        if (sub) centrifugeRef.current?.removeSubscription(sub);
        subscriptionsRef.current.delete(channel);
        handlerCountRef.current.delete(channel);
      } else {
        handlerCountRef.current.set(channel, remaining);
      }
    };
  }, []);

  return (
    <CentrifugoContext.Provider value={{ isConnected, channelPrefix, subscribe }}>
      {children}
    </CentrifugoContext.Provider>
  );
};

export const useCentrifugo = (): CentrifugoContextValue => {
  const context = useContext(CentrifugoContext);
  if (!context) {
    // Return no-op for Storybook/testing environments
    return {
      isConnected: false,
      channelPrefix: CHANNEL_PREFIX,
      subscribe: () => () => {},
    };
  }
  return context;
};
